import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Send } from 'lucide-react';
import { format } from 'date-fns';

export default function CreateRFPPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    event_name: '',
    event_type: '',
    check_in_date: null,
    check_out_date: null,
    location: '',
    attendees: '',
    budget_range: '',
    requirements: ''
  });

  const eventTypes = [
    'Conference',
    'Product Launch',
    'AGM',
    'Team Offsite',
    'Training',
    'Dealer Meet',
    'Sales Kickoff',
    'Board Meeting',
    'Other'
  ];

  const budgetRanges = [
    'Under 5 Lakhs',
    '5-10 Lakhs',
    '10-25 Lakhs',
    '25-50 Lakhs',
    '50 Lakhs - 1 Crore',
    'Above 1 Crore'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Format dates for backend
      const submissionData = {
        ...formData,
        start_date: formData.check_in_date ? format(formData.check_in_date, 'yyyy-MM-dd') : '',
        end_date: formData.check_out_date ? format(formData.check_out_date, 'yyyy-MM-dd') : '',
      };
      
      // Remove date objects
      delete submissionData.check_in_date;
      delete submissionData.check_out_date;
      
      await api.createRFP(submissionData);
      toast.success('RFP created successfully! Vendors will start bidding soon.');
      navigate('/dashboard/client/rfps');
    } catch (error) {
      toast.error('Failed to create RFP');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard/client')}
          className="mb-4 sm:mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <Card className="border-2 border-navy-200">
          <CardHeader className="bg-gradient-to-r from-navy-600 to-navy-800 text-white p-4 sm:p-6">
            <CardTitle className="text-xl sm:text-2xl">Create New RFP</CardTitle>
            <CardDescription className="text-navy-100 text-sm sm:text-base">
              Request quotes from multiple vendors for your event
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Event Name *
                  </label>
                  <Input
                    required
                    placeholder="e.g., Annual Sales Conference 2025"
                    value={formData.event_name}
                    onChange={(e) => handleChange('event_name', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Event Type *
                  </label>
                  <Select
                    value={formData.event_type}
                    onValueChange={(value) => handleChange('event_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-900 mb-3">
                  Event Dates *
                </label>
                <DateRangePicker
                  checkInDate={formData.check_in_date}
                  checkOutDate={formData.check_out_date}
                  onCheckInChange={(date) => handleChange('check_in_date', date)}
                  onCheckOutChange={(date) => handleChange('check_out_date', date)}
                  className="w-full"
                />
              </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Location *
                  </label>
                  <Input
                    required
                    placeholder="e.g., Mumbai, Delhi, Bangalore"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Number of Attendees *
                  </label>
                  <Input
                    required
                    type="number"
                    placeholder="e.g., 150"
                    value={formData.attendees}
                    onChange={(e) => handleChange('attendees', e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">
                    Budget Range *
                  </label>
                  <Select
                    value={formData.budget_range}
                    onValueChange={(value) => handleChange('budget_range', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget range" />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetRanges.map(range => (
                        <SelectItem key={range} value={range}>{range}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 mb-2">
                  Requirements & Special Requests *
                </label>
                <Textarea
                  required
                  rows={6}
                  placeholder="Describe your requirements:\n- Venue capacity and layout\n- F&B requirements\n- AV equipment needed\n- Accommodation needs\n- Any special arrangements"
                  value={formData.requirements}
                  onChange={(e) => handleChange('requirements', e.target.value)}
                  className="font-mono text-sm"
                />
              </div>

              <div className="bg-navy-50 p-4 rounded-lg border border-navy-200">
                <h4 className="font-semibold text-navy-800 mb-2">What happens next?</h4>
                <ul className="text-sm text-slate-800 space-y-1">
                  <li>• Multiple vendors will submit competitive bids</li>
                  <li>• You can compare quotes side-by-side</li>
                  <li>• Negotiate directly with shortlisted vendors</li>
                  <li>• Send final proposal for management approval</li>
                  <li>• Booking is automatically created upon approval</li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard/client')}
                  className="flex-1 w-full"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 w-full bg-navy-600 hover:bg-navy-700"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {loading ? 'Creating RFP...' : 'Create RFP & Get Quotes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}